import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import DashboardSidebar from "@/pages/dashboard/components/DashboardSidebar";
import MobileBottomNav from "@/pages/dashboard/components/MobileBottomNav";

export default function SupportPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a18] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-violet-400 text-3xl"></i>
      </div>
    );
  }

  if (!user) return null;

  const sidebarWidth = collapsed ? "md:ml-16" : "md:ml-60";

  return (
    <div className="min-h-screen bg-[#080812] flex">
      <DashboardSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />

      <main className={`flex-1 ${sidebarWidth} transition-all duration-300 min-h-screen pb-20 md:pb-0`}>
        {/* Top bar */}
        <header className="h-14 md:h-16 bg-[#0a0a18]/80 backdrop-blur-md border-b border-white/[0.06] flex items-center justify-between px-4 md:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-line text-base md:text-lg"></i>
            </button>
            <div>
              <h1 className="text-white font-semibold text-sm md:text-base">고객 지원</h1>
              <p className="text-gray-500 text-[10px] md:text-xs">Signal SMS 고객센터</p>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 md:p-6 space-y-4 md:space-y-6 max-w-4xl">
          {/* Telegram CTA */}
          <div className="relative overflow-hidden rounded-2xl border border-[#24A1DE]/20 bg-gradient-to-br from-[#24A1DE]/10 to-[#24A1DE]/5 p-4 md:p-6">
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#24A1DE]/10 rounded-full blur-3xl"></div>
            <div className="relative flex flex-col md:flex-row items-center gap-4 md:gap-6">
              <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-2xl bg-[#24A1DE]/20 shrink-0">
                <i className="ri-telegram-fill text-[#24A1DE] text-2xl md:text-3xl"></i>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-white font-semibold text-base md:text-lg mb-1">실시간 텔레그램 상담</h2>
                <p className="text-gray-400 text-xs md:text-sm leading-relaxed">
                  가장 빠른 답변을 원하신다면 텔레그램으로 연락해 주세요.
                  <br className="hidden md:block" />
                  24시간 상담 가능합니다.
                </p>
              </div>
              <a
                href="https://t.me/signal_sms"
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 inline-flex items-center gap-2 px-4 md:px-5 py-2 md:py-2.5 rounded-xl bg-[#24A1DE] hover:bg-[#1d8fc7] text-white text-xs md:text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-telegram-line text-sm md:text-base"></i>
                @signal_sms 바로가기
              </a>
            </div>
          </div>

          {/* Contact cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
            <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 md:p-5">
              <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-violet-500/10 mb-2 md:mb-3">
                <i className="ri-mail-line text-violet-400 text-base md:text-lg"></i>
              </div>
              <h3 className="text-white text-sm font-medium mb-1">이메일</h3>
              <p className="text-gray-400 text-xs">support@signalsms.com</p>
              <p className="text-gray-600 text-[10px] md:text-xs mt-1">24시간 접수 가능</p>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 md:p-5">
              <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-pink-500/10 mb-2 md:mb-3">
                <i className="ri-time-line text-pink-400 text-base md:text-lg"></i>
              </div>
              <h3 className="text-white text-sm font-medium mb-1">운영 시간</h3>
              <p className="text-gray-400 text-xs">24시간 상담 가능</p>
              <p className="text-gray-600 text-[10px] md:text-xs mt-1">연중무휴</p>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 md:p-5 flex flex-col items-center text-center">
              <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-emerald-500/10 mb-2 md:mb-3">
                <i className="ri-telegram-line text-emerald-400 text-base md:text-lg"></i>
              </div>
              <h3 className="text-white text-sm font-medium mb-1">텔레그램</h3>
              <p className="text-gray-400 text-xs mb-3">@signal_sms</p>
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-lg overflow-hidden border border-white/[0.1] bg-white p-1.5">
                <img
                  src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://t.me/signal_sms"
                  alt="텔레그램 QR 코드"
                  className="w-full h-full object-contain"
                />
              </div>
              <p className="text-gray-600 text-[10px] md:text-xs mt-2">QR 코드로 바로 연락</p>
            </div>
          </div>

          {/* Telegram bottom banner */}
          <div className="flex items-center justify-center gap-2 py-3 md:py-4">
            <span className="text-gray-600 text-[10px] md:text-xs">더 빠른 상담이 필요하신가요?</span>
            <a
              href="https://t.me/signal_sms"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[#24A1DE] hover:text-[#1d8fc7] text-[10px] md:text-xs font-medium transition-colors cursor-pointer"
            >
              <i className="ri-telegram-line text-xs md:text-sm"></i>
              텔레그램 @signal_sms
            </a>
          </div>
        </div>
      </main>

      <MobileBottomNav />
    </div>
  );
}